import { ApiError as ContractsApiError } from '@adverity/contracts'
import * as Sentry from '@sentry/react'
import type { AxiosError } from 'axios'
import { create, isAxiosError } from 'axios'
import Cookies from 'js-cookie'

export type ApiError<T = unknown> = AxiosError<T>

export const isApiError = <T = unknown>(error: unknown): error is ApiError<T> => !!error && isAxiosError(error)

/** @public we have tests for this */
export const paramsSerializer = (params: Record<string, string | Array<string>>) => {
    if ('queryString' in params && typeof params.queryString === 'string') {
        return params.queryString
    }

    const paramsNormalized = Object.entries(params).flatMap(([key, value]) => {
        if (Array.isArray(value)) {
            return value.map((singleValue) => [key, singleValue])
        }

        return [[key, value]]
    })

    return new URLSearchParams(paramsNormalized).toString()
}

export const retryWhenServerError = (failureCount: number, error: unknown): boolean => {
    let status: number | undefined

    if (isApiError(error)) {
        status = error.response?.status
    }

    if (error instanceof ContractsApiError) {
        status = error.status
    }

    return failureCount < 3 && (!status || status >= 500)
}

/**
 * The only used API approach in the app.
 */
const client = create({
    withCredentials: true,
    paramsSerializer: { serialize: paramsSerializer },
})

client.interceptors.request.use((config) => {
    const currentSpan = Sentry.getActiveSpan() ?? Sentry.startInactiveSpan({ name: 'Manual Span' })
    const sentryTraceHeader = Sentry.spanToTraceHeader(currentSpan)
    const sentryBaggageHeader = Sentry.spanToBaggageHeader(currentSpan)
    // Read the CSRF cookie per request so the rotated value is sent after
    // Django regenerates the token (login, re-auth). Reading it once at
    // module load freezes the original token for the SPA's lifetime and
    // causes 403 "CSRF check Failed" on every unsafe request after rotation.
    const csrftoken = Cookies.get('csrftoken')

    if (sentryTraceHeader) config.headers.set('sentry-trace', sentryTraceHeader)

    if (sentryBaggageHeader) config.headers.set('baggage', sentryBaggageHeader)

    if (csrftoken) config.headers.set('X-CSRFToken', csrftoken)

    return config
})

export default client
